import * as React from 'react'
import i18next from 'i18next'
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption } from '@headlessui/react'
import {
  MagnifyingGlassIcon,
  RectangleStackIcon,
  UserIcon,
  XMarkIcon as CloseIcon,
} from '@heroicons/react/24/outline'
import AlbumModel from '../../../models/album'
import { IPerson } from '../../../services/api'

interface SearchComboboxProps {
  searchValue: string
  onSearchValueChange: (value: string) => void
  /** Run the phrase search (current behavior of the search bar) */
  onSubmitSearch: () => void
  onClear: () => void
  /** Albums matching the typed phrase */
  albums: Array<AlbumModel>
  onSelectAlbum: (album: AlbumModel) => void
  /** Persons matching the typed phrase */
  persons: Array<IPerson>
  onSelectPerson: (person: IPerson) => void
  inputRef: React.RefObject<HTMLInputElement>
  /** true while the search should take the whole mobile header (focused or has text) */
  onExpandChange?: (expanded: boolean) => void
}

type SearchSelection =
  | { kind: 'phrase' }
  | { kind: 'album'; album: AlbumModel }
  | { kind: 'person'; person: IPerson }

// taller rows on mobile for comfortable touch targets (~48px)
const optionClasses = `group flex cursor-default select-none items-center gap-3 px-4 py-3.5
  text-gray-900 data-focus:bg-brand data-focus:text-white md:gap-2 md:px-3 md:py-2`

const optionIconClasses = 'h-5 w-5 shrink-0 text-gray-400 group-data-focus:text-white md:h-4 md:w-4'

export const SearchCombobox: React.FC<SearchComboboxProps> = ({
  searchValue,
  onSearchValueChange,
  onSubmitSearch,
  onClear,
  albums,
  onSelectAlbum,
  persons,
  onSelectPerson,
  inputRef,
  onExpandChange,
}) => {
  const [isFocused, setIsFocused] = React.useState(false)
  const isSearchActive = searchValue.trim().length > 0
  // stays expanded while there is text, so blurring into the results list
  // doesn't collapse the header mid-interaction
  const isExpanded = isFocused || isSearchActive

  React.useEffect(() => {
    onExpandChange?.(isExpanded)
  }, [isExpanded])

  const handleSelect = (selection: SearchSelection | null) => {
    if (selection === null) return
    switch (selection.kind) {
      case 'phrase':
        onSubmitSearch()
        break
      case 'album':
        onSelectAlbum(selection.album)
        break
      case 'person':
        onSelectPerson(selection.person)
        break
      default:
        break
    }
  }

  return (
    <div
      className={`flex min-w-0 flex-1 items-center transition-all md:ml-4 md:max-w-md ${
        isExpanded ? 'ml-0 max-w-none' : 'ml-4 max-w-md'
      }`}
    >
      {/* mobile: close button outside the input, collapses the expanded search */}
      {isExpanded && (
        <button
          type="button"
          onClick={onClear}
          className="-ml-1 mr-1 flex shrink-0 items-center justify-center rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 md:hidden"
        >
          <span className="sr-only">Zamknij wyszukiwanie</span>
          <CloseIcon className="h-6 w-6" />
        </button>
      )}

      <div className="relative min-w-0 flex-1">
      <Combobox value={null as SearchSelection | null} onChange={handleSelect}>
        {({ open }) => (
          <>
          <ComboboxInput
            ref={inputRef}
            defaultValue={searchValue}
            onChange={(e) => onSearchValueChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' && !open) onSubmitSearch()
            }}
            placeholder={i18next.t('searchMenu.placeholder')}
            className="block w-full rounded-lg border-0 bg-gray-100 py-2 pl-4 pr-10 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 text-sm leading-6"
          />
          {/* mobile: the left button handles closing, so the right icon just submits */}
          <button
            type="button"
            onClick={onSubmitSearch}
            className="absolute inset-y-0 right-0 flex items-center pr-3 md:hidden"
          >
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
          <button
            type="button"
            onClick={isSearchActive ? onClear : onSubmitSearch}
            className="absolute inset-y-0 right-0 hidden items-center pr-3 md:flex"
          >
            {isSearchActive ? (
              <CloseIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            )}
          </button>

          {/* mobile: instant feedback on focus, before anything is typed */}
          {!isSearchActive && isFocused && (
            <div className="fixed inset-x-0 bottom-0 top-16 z-20 border-t border-gray-100 bg-white py-1 text-sm md:hidden">
              <p className="px-4 py-3.5 text-gray-400">{i18next.t('filters.typeToSearch')}</p>
            </div>
          )}

          {isSearchActive && (
            <ComboboxOptions
              // mobile: full-width panel filling from the 64px header to the bottom;
              // desktop: anchored to the input as before
              className="fixed inset-x-0 bottom-0 top-16 z-20 overflow-auto border-t border-gray-100 bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none md:absolute md:inset-x-auto md:bottom-auto md:top-auto md:mt-1 md:max-h-72 md:w-full md:rounded-md md:border-t-0"
            >
              <ComboboxOption value={{ kind: 'phrase' }} className={optionClasses}>
                <MagnifyingGlassIcon className={optionIconClasses} />
                <span className="truncate">
                  {i18next.t('searchMenu.searchFor', { phrase: searchValue.trim() })}
                </span>
              </ComboboxOption>

              {albums.map((album) => (
                <ComboboxOption key={album.id} value={{ kind: 'album', album }} className={optionClasses}>
                  <RectangleStackIcon className={optionIconClasses} />
                  <span className="truncate">{album.name}</span>
                </ComboboxOption>
              ))}

              {persons.map((person) => (
                <ComboboxOption
                  key={person.id}
                  value={{ kind: 'person', person }}
                  className={optionClasses}
                >
                  {person.cover_url ? (
                    <img
                      src={person.cover_url}
                      alt=""
                      loading="lazy"
                      className="h-6 w-6 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <UserIcon className={optionIconClasses} />
                  )}
                  <span className="truncate">{person.name}</span>
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          )}
          </>
        )}
      </Combobox>
      </div>
    </div>
  )
}
